import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTrash,
  FaListAlt,
  FaCalendarAlt,
  FaCheck,
  FaTimes,
  FaCreditCard,
  FaReceipt,
  FaEye,
  FaSearch,
  FaMoneyBillWave,
  FaFilter,
  FaHistory,
} from "react-icons/fa";
import Pagination from "Components/Pagination";
import CommonModal from "Components/CommonModal";
import { catchError, changeState, enterFn } from "JS/common";
import { getPaymentHistoryList, deleteAddPayment } from "JS/API";
import { useMarkMenuSeen } from "Contexts/NewCountContext";
import PaymentModal from "Components/PaymentModal";
import TaxInfoModal from "Components/TaxInfoModal";
import RefundModal from "Components/Agencies/RefundModal";
import { detailPageStyles, listPageStyles } from "utils/designSystem";

const PaymentsHistoryList = () => {
  // State declarations
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    totalPage: 1,
    limit: 10,
  });
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const [list, setList] = useState([]);
  const [modal] = useState(false);
  const [searchTxt, setSearchTxt] = useState(null);
  const [filterType, setType] = useState(null);
  const [payModal, setPayModal] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [isOpenTaxModal, setIsOpenTaxModal] = useState(false);
  const [taxModalData, setTaxModalData] = useState(null);
  const [searchState, setSearchState] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();
  useMarkMenuSeen("payments");
  let prevent = false;

  // 상태 번역 매핑
  const paidStatusTranslation = {
    paid: "결제 완료",
    canceled: "결제 취소",
    failed: "결제 실패",
    processing: "결제 처리중",
    pending: "대기중",
    refund: "환불 완료",
    partial_refund: "부분 환불",
    ready: "결제 대기",
    refund_request: "환불 요청",
  };

  const paidTypeTranslation = {
    toss: "TOSS 결제",
    bank: "무통장 입금",
  };

  // 상태별 색상 매핑
  const statusColors = {
    paid: { bg: '#dcfce7', color: '#166534', icon: FaCheck },
    canceled: { bg: '#fef2f2', color: '#991b1b', icon: FaTimes },
    failed: { bg: '#fef2f2', color: '#991b1b', icon: FaTimes },
    processing: { bg: '#fef3c7', color: '#92400e', icon: FaHistory },
    pending: { bg: '#e0e7ff', color: '#3730a3', icon: FaHistory },
    refund: { bg: '#f3e8ff', color: '#6b21a8', icon: FaMoneyBillWave },
    partial_refund: { bg: '#fae8ff', color: '#9333ea', icon: FaMoneyBillWave },
    ready: { bg: '#e0f2fe', color: '#0369a1', icon: FaHistory },
    refund_request: { bg: '#fff7ed', color: '#c2410c', icon: FaMoneyBillWave },
  };

  // 필터 옵션
  const filterOptions = [
    { value: "all", label: "전체보기", icon: FaListAlt },
    { value: "ready", label: "결제 대기", icon: FaHistory },
    { value: "processing", label: "처리중", icon: FaHistory },
    { value: "failed", label: "결제 실패", icon: FaTimes },
    { value: "canceled", label: "결제 취소", icon: FaTimes },
    { value: "paid", label: "결제 완료", icon: FaCheck },
    { value: "refund_request", label: "환불 요청", icon: FaMoneyBillWave },
    { value: "refund", label: "환불 완료", icon: FaMoneyBillWave },
    { value: "partial_refund", label: "부분 환불", icon: FaMoneyBillWave },
  ];

  const limitOptions = [
    { value: 10, label: "10개씩 보기" },
    { value: 30, label: "30개씩 보기" },
    { value: 50, label: "50개씩 보기" },
  ];

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 스타일 정의
  const styles = {
    container: listPageStyles.container,
    header: listPageStyles.header,
    title: detailPageStyles.title,
    controlsSection: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      border: '1px solid #cbd5e1',
      marginBottom: '24px',
      padding: '20px'
    },
    controlsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      alignItems: 'end'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    },
    label: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151'
    },
    select: {
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box'
    },
    searchContainer: {
      display: 'flex',
      gap: '8px'
    },
    searchInput: {
      flex: 1,
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
    },
    searchButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      whiteSpace: 'nowrap'
    },
    tableSection: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      border: '1px solid #cbd5e1',
      overflow: 'hidden'
    },
    tableWrapper: detailPageStyles.tableWrapper,
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '800px'
    },
    th: {
      padding: '16px 12px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: '600',
      color: '#1e293b',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      backgroundColor: '#f8fafc',
      borderBottom: '2px solid #e2e8f0'
    },
    td: {
      padding: '16px 12px',
      fontSize: '14px',
      color: '#1e293b',
      borderBottom: '1px solid #f1f5f9',
      verticalAlign: 'top'
    },
    userInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    userName: {
      fontWeight: '600',
      color: '#1e293b'
    },
    userId: {
      fontSize: '12px',
      color: '#6b7280',
      fontFamily: 'monospace'
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '6px 10px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500'
    },
    paymentType: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      backgroundColor: '#f1f5f9',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      color: '#374151'
    },
    amount: {
      fontWeight: '600',
      color: '#059669',
      fontSize: '14px'
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap'
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 12px',
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      whiteSpace: 'nowrap'
    },
    buttonSecondary: {
      backgroundColor: '#6b7280',
      color: '#ffffff'
    },
    buttonDanger: {
      backgroundColor: '#dc2626',
      color: '#ffffff'
    },
    emptyState: {
      textAlign: 'center',
      padding: '48px 24px',
      color: '#6b7280'
    },
    emptyStateIcon: {
      fontSize: '48px',
      marginBottom: '16px',
      color: '#d1d5db'
    },
    emptyStateText: {
      fontSize: '16px',
      fontWeight: '500'
    },
    taxInvoiceStatus: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500'
    },
    taxInvoiceIssued: {
      backgroundColor: '#dcfce7',
      color: '#166534'
    },
    taxInvoiceNotIssued: {
      backgroundColor: '#f3f4f6',
      color: '#6b7280'
    }
  };

  // API 호출 함수들
  const getPaymentsHistory = async () => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => {
      prevent = false;
    }, 200);

    const result = await getPaymentHistoryList(
      pageInfo.page,
      pageInfo.limit,
      filterType,
      searchTxt
    );
    
    if (typeof result === "object") {
      setList(result?.data?.data);
      changeState(setPageInfo, "totalPage", result?.data?.meta?.total_page);
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  const userSearch = async () => {
    if (searchState === true) {
      setSearchState(false);
    } else {
      setSearchState(true);
    }
  };

  const handleDeleteAddPayment = async (add_payment_pk) => {
    const isConfirmed = window.confirm("정말 이 결제 정보를 삭제하시겠습니까?");

    if (!isConfirmed) {
      return;
    }

    try {
      const result = await deleteAddPayment(add_payment_pk);

      if (typeof result === "object") {
        setAlertBox({
          mode: "alert",
          context: "결제 정보가 성공적으로 삭제되었습니다.",
          bool: true,
        });

        prevent = false;
        await getPaymentsHistory();
      } else {
        return await catchError(result, navigate, setAlertBox, setAlert);
      }
    } catch (error) {
      console.error("Delete operation failed:", error);
      setAlertBox({
        mode: "alert",
        context: "결제 정보 삭제 중 오류가 발생했습니다.",
        bool: true,
      });
    }
  };

  const handleEditClick = (paymentData) => {
    const {
      paid_reason,
      paid_reason_detail,
      merchant_price,
      ended_at,
      merchant_vat,
      add_payment_pk,
      user_pk,
      biz_license_filename,
      biz_license_original_filename,
      biz_license_path,
      biz_regist_number,
      company,
      created_at,
      email,
      merchant_total_price,
      owner,
      paid_at,
      paid_method,
      paid_status,
      paid_type,
      phone,
      tax_invoice_type,
      user_id,
      user_name,
    } = paymentData;

    setEditPayment({
      paid_reason,
      paid_reason_detail,
      merchant_price,
      ended_at,
      merchant_vat,
      add_payment_pk,
      user_pk,
    });

    setTaxModalData({
      add_payment_pk,
      biz_license_filename,
      biz_license_original_filename,
      biz_license_path,
      biz_regist_number,
      company,
      created_at,
      email,
      ended_at,
      merchant_price,
      merchant_total_price,
      merchant_vat,
      owner,
      paid_at,
      paid_method,
      paid_reason,
      paid_reason_detail,
      paid_status,
      paid_type,
      phone,
      tax_invoice_type,
      user_id,
      user_name,
      user_pk,
    });

    setPayModal(true);
  };

  // useEffect 훅들
  useEffect(() => {
    document.title = "마크픽 관리자 > 결제 내역 관리";
  }, []);

  useEffect(() => {
    if (!modal) {
      getPaymentsHistory();
    }
  }, [pageInfo.page, pageInfo.limit, modal, searchState]);


  // 렌더링 함수들
  const renderTableBody = () => {
    return list.map((paymentData) => {
      const {
              id,
              user_id,
              user_name,
              paid_method,
              paid_status,
              paid_at,
              paid_type,
              merchant_total_price,
              merchant_price,
              add_payment_pk,
              tax_invoice_type,
            } = paymentData;

      const statusConfig = statusColors[paid_status] || statusColors.pending;
      const StatusIcon = statusConfig.icon;

      return (
        <tr key={id}>
          <td style={styles.td}>
            <div style={styles.userInfo}>
              <div style={styles.userName}>{user_name}</div>
              <div style={styles.userId}>({user_id})</div>
            </div>
          </td>

          <td style={styles.td}>
            <div style={styles.paymentType}>
              <FaCreditCard size={12} />
              {paid_type}
            </div>
          </td>

          <td style={styles.td}>
            <div style={styles.paymentType}>
              <FaMoneyBillWave size={12} />
              {paidTypeTranslation[paid_method] || paid_method}
            </div>
          </td>

          <td style={styles.td}>
            <div style={styles.amount}>
              {merchant_total_price?.toLocaleString()}원
            </div>
            {(paid_status === 'partial_refund' || paid_status === 'refund') && paymentData.refund_amount > 0 && (
              <div style={{ fontSize: '11px', marginTop: '2px' }}>
                <span style={{ color: '#dc2626' }}>환불 {paymentData.refund_amount?.toLocaleString()}원</span>
                {paid_status === 'partial_refund' && (
                  <span style={{ color: '#2563eb', marginLeft: '6px' }}>잔액 {paymentData.balance_amount?.toLocaleString()}원</span>
                )}
              </div>
            )}
          </td>

          <td style={styles.td}>
            <div
              style={{
                ...styles.statusBadge,
                backgroundColor: statusConfig.bg,
                color: statusConfig.color
              }}
            >
              <StatusIcon size={12} />
              {paidStatusTranslation[paid_status] || paid_status}
            </div>
          </td>

          <td style={styles.td}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FaCalendarAlt size={12} color="#6b7280" />
              {paid_at ? paid_at.split("T")[0] : "-"}
            </div>
          </td>

          <td style={styles.td}>
            {tax_invoice_type === "private" || tax_invoice_type === "business" ? (
              <div style={{...styles.taxInvoiceStatus, ...styles.taxInvoiceIssued}}>
                <FaReceipt size={12} />
                발행
              </div>
            ) : (
              <div style={{...styles.taxInvoiceStatus, ...styles.taxInvoiceNotIssued}}>
                <FaTimes size={12} />
                미발행
              </div>
            )}
          </td>

          <td style={styles.td}>
            {paid_type === "추가결제" ? (
              <div style={styles.actionButtons}>
                <button
                  style={styles.button}
                  onClick={() => handleEditClick(paymentData)}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                >
                  <FaEye />
                  자세히보기
                </button>
                {paid_status !== "refund" && paid_status !== "canceled" && paymentData.order_id && (
                  <button
                    style={{...styles.button, backgroundColor: '#7c3aed', color: '#fff'}}
                    onClick={() => {
                      setRefundTarget({
                        paid_amount: merchant_total_price || merchant_price,
                        merchant_price: merchant_total_price || merchant_price,
                        balance_amount: paymentData.balance_amount ?? (merchant_total_price || merchant_price),
                        paid_method: paid_method,
                        paid_status: paid_status,
                        toss_info: paymentData.toss_info || { order_id: paymentData.order_id },
                      });
                      setRefundModal(true);
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#6d28d9'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#7c3aed'}
                  >
                    <FaMoneyBillWave />
                    환불
                  </button>
                )}
                <button
                  style={{...styles.button, ...styles.buttonDanger}}
                  onClick={() => handleDeleteAddPayment(add_payment_pk)}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                >
                  <FaTrash />
                  삭제
                </button>
              </div>
            ) : (
              <span style={{ color: '#6b7280', fontSize: '14px' }}>-</span>
            )}
          </td>
        </tr>
      );
    });
  };

  const renderEmptyState = () => (
    <div style={styles.emptyState}>
      <div style={styles.emptyStateIcon}>
        <FaHistory />
      </div>
      <div style={styles.emptyStateText}>결제 내역이 없습니다.</div>
    </div>
  );

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaMoneyBillWave />
          결제 내역 관리
        </h1>
      </div>

      {/* 컨트롤 섹션 */}
      <div style={styles.controlsSection}>
        <div style={styles.controlsGrid}>
          {/* 페이지 크기 선택 */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <FaListAlt style={{ marginRight: '4px' }} />
              페이지 크기
            </label>
            <select
              style={styles.select}
              value={pageInfo.limit}
              onChange={(e) => {
                setPageInfo((prev) => ({
                  ...prev,
                  page: 1,
                  limit: parseInt(e.target.value)
                }));
              }}
            >
              {limitOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* 상태 필터 */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <FaFilter style={{ marginRight: '4px' }} />
              결제 상태
            </label>
            <select
              style={styles.select}
              value={filterType || "all"}
              onChange={(e) => {
                setType(e.target.value === "all" ? null : e.target.value);
                changeState(setPageInfo, "page", 1);
              }}
            >
              {filterOptions.map((option) => {
                return (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                );
              })}
            </select>
          </div>

          {/* 검색 */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <FaSearch style={{ marginRight: '4px' }} />
              사용자 검색
            </label>
            <div style={styles.searchContainer}>
              <input
                type="text"
                style={styles.searchInput}
                placeholder="검색할 아이디 또는 이름을 입력하세요"
                value={searchTxt || ""}
                onChange={(e) => setSearchTxt(e.target.value)}
                onKeyDown={(e) => {
                  if (!alertBox.bool) enterFn(e, userSearch);
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                style={styles.searchButton}
                onClick={userSearch}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                <FaSearch />
                검색
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 테이블 섹션 */}
      <div style={styles.tableSection}>
        {list?.length ? (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>사용자</th>
                  <th style={styles.th}>결제 유형</th>
                  <th style={styles.th}>결제 수단</th>
                  <th style={styles.th}>결제 금액</th>
                  <th style={styles.th}>결제 상태</th>
                  <th style={styles.th}>결제일</th>
                  <th style={styles.th}>세금계산서</th>
                  <th style={styles.th}>작업</th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </table>
          </div>
        ) : (
          renderEmptyState()
        )}
      </div>

      {/* 페이지네이션 */}
      {list?.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />
        </div>
      )}

      {/* 모달들 */}
      {payModal && (
        <PaymentModal
          setPayModal={setPayModal}
          userPk={editPayment?.user_pk}
          applyPaymentPk={editPayment?.add_payment_pk}
          editData={editPayment}
          setEditData={setEditPayment}
          onSuccess={getPaymentsHistory}
          taxInfo={taxModalData}
        />
      )}

      {refundModal && refundTarget && (
        <RefundModal
          info={refundTarget}
          onClose={() => {
            setRefundModal(false);
            setRefundTarget(null);
          }}
          onSuccess={getPaymentsHistory}
        />
      )}

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "logout") navigate("/");
            else return;
          }}
        />
      )}

      {isOpenTaxModal && taxModalData && (
        <TaxInfoModal
          setIsOpenTaxModal={setIsOpenTaxModal}
          taxInfo={taxModalData}
        />
      )}

      {/* 반응형 스타일 */}
      <style>{`
        @media (max-width: 768px) {
          .table-wrapper {
            -webkit-overflow-scrolling: touch;
          }
          
          .table-wrapper::-webkit-scrollbar {
            height: 6px;
          }
          
          .table-wrapper::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 3px;
          }
          
          .table-wrapper::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 3px;
          }
        }

        .search-input:focus {
          outline: none;
        }

        .button:hover {
          transform: translateY(-1px);
        }

        .status-badge {
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .user-info:hover .user-name {
          color: #3b82f6;
        }

        .action-buttons button {
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .payment-type:hover {
          background-color: #e2e8f0;
        }

        .amount {
          font-variant-numeric: tabular-nums;
        }

        .table tbody tr:hover {
          background-color: #f8fafc;
        }

        .controls-grid {
          align-items: end;
        }

        @media (max-width: 1024px) {
          .controls-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          
          .search-container {
            flex-direction: column;
            gap: 8px;
          }
          
          .search-button {
            width: 100%;
            justify-content: center;
          }
          
          .action-buttons {
            flex-direction: column;
            gap: 4px;
          }
          
          .action-buttons button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentsHistoryList;